import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, type PickedTitle } from '../api'
import { useI18n } from '../i18n'
import type { CatalogType } from '../protocol'
import { Icon } from '../ui/Icon'
import { CatalogSearch } from './Search'
import { TitleView } from './Title'

/**
 * Catalog over the room: the owner finds a title and either chooses it alone
 * (a release can come later, or by hand) or picks a release right away.
 * initial opens it on a title, e.g. to pick a release for the chosen one.
 */
export function CatalogDialog({ roomId, initial, onClose }: { roomId: string; initial?: PickedTitle; onClose: () => void }) {
  const { t } = useI18n()
  const [title, setTitle] = useState<{ type: CatalogType; id: number; season?: number } | null>(initial ?? null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Escape inside the search closes its suggestions first.
      if (e.key === 'Escape' && !(e.target as HTMLElement).closest('.csearch')) onClose()
    }
    document.addEventListener('keydown', onKey)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [onClose])

  useEffect(() => {
    ref.current?.scrollTo({ top: 0 })
  }, [title])

  return createPortal(
    <div className="cdialog" role="dialog" aria-modal="true" aria-label={t('catalog.find')}>
      <div className="cdialog__bar">
        {title && (
          <button type="button" className="icon-btn" onClick={() => setTitle(null)} aria-label={t('catalog.back')}>
            <Icon name="back" />
          </button>
        )}
        <CatalogSearch onPick={(it) => setTitle({ type: it.type, id: it.id })} autoFocus={!title} />
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('catalog.close')}>
          <Icon name="close" />
        </button>
      </div>
      <div className="cdialog__body" ref={ref}>
        {title ? (
          <TitleView
            key={`${title.type}${title.id}`}
            type={title.type}
            id={title.id}
            initialSeason={title.season}
            actionLabel={t('catalog.watch')}
            onChoose={async (picked) => {
              await api.addRelease(roomId, picked)
              onClose()
            }}
            onSelect={async (picked) => {
              await api.setTitle(roomId, picked)
              onClose()
            }}
          />
        ) : (
          <p className="cdialog__hint muted">{t('catalog.hint')}</p>
        )}
      </div>
    </div>,
    document.body,
  )
}
